import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '../dates.ts';
import { townLabel } from '../town.ts';
import {
  useInactivePreview, useProperty, useRecordDisposition, useWithdrawDisposition,
  type DispositionDone, type DispositionEvent, type PropertyDetail, type PropertyRecord,
} from '../api/properties.ts';
import { LoadFailure } from './LoadFailure.tsx';
import { PropertyContacts } from './contacts/PropertyContacts.tsx';
import { OccupiedFlag, Refusal } from './PropertiesPanel.tsx';
import { SiteVisits } from './SiteVisits.tsx';
import { RecordEdit } from './RecordEdit.ts';
import { DiscardAsk, useDismissGuard } from './dismissGuard.tsx';
import { propertyPath } from '../tasks/taskView.ts';

const field =
  'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 ' +
  'text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';
const hint = 'mt-1 text-sm text-neutral-600';
const secondary =
  'min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold ' +
  'text-neutral-800 hover:bg-neutral-50 active:bg-neutral-100';
const primary =
  'min-h-[44px] rounded-lg bg-neutral-900 px-4 text-sm font-semibold text-white disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed';

function todayLocal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * One property, in a modal (Arthur, 6 and 7 October): the record, its
 * disposition history, and one action, Record a disposition: any of the eleven,
 * dated, or said in so many words to have no known date. Preparing to
 * Surrender is offered because several properties are in that state.
 *
 * **Sold and Surrendered make the property INACTIVE in the same step** (096).
 * There is no state between "sold" and "not ours", and the step cannot be
 * undone, so choosing either shows what follows, as the server computes it from
 * the functions its triggers use, and asks for confirmation before anything is
 * recorded. A mis-click on "Surrendered" is exactly what happens in week one.
 * The third path into inactive, a client's discharge, is the court's: it
 * happens on its own when the discharge is recorded.
 *
 * Native `<dialog>`, as the facility board's: Escape closes it and focus stays
 * inside. The mortgage, tax and insurance fields are not shown: they are empty
 * until the real estate module.
 */
export function PropertyModal({ id, onClose, recordVisit = false, showVisit = null }: {
  id: string; onClose: () => void; recordVisit?: boolean; showVisit?: string | null;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const closed = useRef(onClose);
  closed.current = onClose;
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const handle = () => closed.current();
    dialog.addEventListener('close', handle);
    if (!dialog.open) dialog.showModal();
    return () => dialog.removeEventListener('close', handle);
  }, []);
  const dismiss = () => {
    if (ref.current?.open) ref.current.close();
    closed.current();
  };
  const q = useProperty(id);
  // A-#195: the backdrop only when pressed and released on it; changes are never discarded silently
  const guard = useDismissGuard(dismiss);

  return (
    <dialog
      ref={ref}
      aria-labelledby="property-dialog-title"
      {...guard.dialogProps}
      className="m-auto w-[min(48rem,calc(100vw-2rem))] rounded-xl bg-panel p-0 text-neutral-900 shadow-xl backdrop:bg-black/40"
    >
      <div className="max-h-[90vh] overflow-y-auto">
        <DiscardAsk guard={guard} />
        {q.data ? (
          <PropertyBody detail={q.data} dismiss={guard.request} recordVisit={recordVisit} showVisit={showVisit} />
        ) : q.error ? (
          <div className="p-4">
            <LoadFailure what="The property" error={q.error} onRetry={() => void q.refetch()} retrying={q.isFetching} forbiddenIsSession={false} />
            <button type="button" className={`${secondary} mt-3`} onClick={guard.request}>Close</button>
          </div>
        ) : (
          <p className="px-4 py-10 text-center text-neutral-600">Loading…</p>
        )}
      </div>
    </dialog>
  );
}

/**
 * A property's record (A-#195 1): its own page under the client (PropertyPage), and the card Real Estate opens. On the
 * page there is no Close (`dismiss` absent) and the address is the page's heading; the card links to the page.
 */
export function PropertyBody({ detail, dismiss, recordVisit, showVisit }: {
  detail: PropertyDetail; dismiss?: () => void; recordVisit: boolean; showVisit: string | null;
}) {
  const p = detail.property;
  const [acting, setActing] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const town = townLabel(p.city, p.neighbourhood, p.neighbourhood_source);
  const mayAct = detail.may_record && !p.discharged;

  return (
    <>
      <div className="flex items-start gap-3 border-b border-neutral-200 px-4 py-3">
        <div className="min-w-0">
          {dismiss ? (
            <h2 id="property-dialog-title" className="text-lg font-semibold text-neutral-900">
              {[p.street_address, p.unit].filter(Boolean).join(' ') || 'No street recorded'}
            </h2>
          ) : (
            <h1 className="text-xl font-bold text-neutral-900">{[p.street_address, p.unit].filter(Boolean).join(' ') || 'No street recorded'}</h1>
          )}
          <p className="text-sm text-neutral-600">
            {[town, [p.state, p.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
          </p>
          <p className="mt-1 text-sm text-neutral-700">
            <Link to={`/clients/${p.client_id}`} state={{ from: 'Real Estate' }} className="font-medium underline underline-offset-2">
              {p.display_name}
            </Link>{' '}
            · {p.client_ref} · {p.legal_status}
          </p>
          {p.occupied && <OccupiedFlag />}
          {dismiss && (
            <p className="mt-1 text-sm">
              <Link to={propertyPath(p.client_id, p.id)} className="link inline-flex min-h-[44px] items-center font-semibold">Open the property's page</Link>
            </p>
          )}
        </div>
        {dismiss && (
          <button
            type="button"
            onClick={dismiss}
            className="ml-auto min-h-[44px] min-w-[44px] rounded-lg px-3 text-sm font-semibold text-neutral-700 ring-1 ring-neutral-300 hover:bg-neutral-50"
          >
            Close
          </button>
        )}
      </div>

      {p.client_deceased && !p.discharged && (
        <div className="border-b border-indigo-200 border-l-4 border-l-indigo-700 bg-indigo-50 px-4 py-2 text-indigo-950">
          <p className="text-xs font-bold tracking-[0.08em] uppercase">Client deceased · work continues</p>
          <p className="mt-1 text-sm">
            {p.display_name} died{p.date_of_death ? ` on ${formatDate(p.date_of_death)}` : ''}, and the agency has not
            discharged this property. Either a court order directs the agency to continue, or a decision about who
            takes it over is waiting. Say which in the notes until the order can be recorded.
          </p>
        </div>
      )}

      <div className="space-y-4 px-4 py-3">
        <p className="text-neutral-800">{occupancy(p)}</p>

        <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
          <Fact term="Property type">{p.property_type_label ?? 'Not recorded'}</Fact>
          <Fact term="Tenure">{p.tenure === 'Unknown' ? 'Not known' : p.tenure}</Fact>
          <Fact term="Disposition">{p.disposition ?? 'None recorded'}</Fact>
          <Fact term="Discharged">
            {p.discharged
              ? `Inactive since ${p.discharged_on ? formatDate(p.discharged_on) : 'a date not known'}${p.client_released ? ': the client was discharged' : ''}`
              : 'Active: the agency still has work on it'}
          </Fact>
          {p.held_since && <Fact term="Held since">{formatDate(p.held_since)}</Fact>}
          <Fact term="County">{p.county ?? 'Not recorded'}</Fact>
          <Fact term="Parcel ID">{p.parcel_id ?? 'Not recorded'}</Fact>
          <Fact term="Lis pendens">{p.lis_pendens_filed ? 'Filed' : 'None recorded'}</Fact>
          <Fact term="Court authority">
            {p.court_auth_required ? 'Required for a sale or disposition' : 'Not required, as recorded'}
          </Fact>
          <Fact term="Legal description" wide>{p.legal_description ?? 'Not recorded'}</Fact>
          <Fact term="Notes" wide>{p.notes ?? 'None'}</Fact>
        </dl>

        {/* 122: recording a visit is open on an inactive property too: a visit made before it was let go counts as history */}
        <SiteVisits detail={detail} mayAct={detail.may_record} startRecording={recordVisit} showVisit={showVisit} />

        <History detail={detail} mayAct={mayAct} />

        <PropertyContacts propertyId={p.id} />

        {said && <p role="status" className="rounded-lg bg-neutral-50 px-3 py-2 text-neutral-800">{said}</p>}

        {mayAct && !acting && (
          <div className="flex flex-wrap gap-2 border-t border-neutral-200 pt-3">
            <button type="button" className={secondary} onClick={() => { setActing(true); setSaid(null); }}>
              Record a disposition
            </button>
          </div>
        )}
        {acting && (
          <DispositionForm detail={detail} onDone={(m) => { setActing(false); setSaid(m); }} />
        )}
      </div>
    </>
  );
}

/** Occupancy in words: derived on every read, never stored. */
function occupancy(p: PropertyRecord): string {
  if (p.discharged && p.client_released) {
    return 'Inactive: the court discharged the client. That says nothing about where they live, which is unchanged.';
  }
  if (p.discharged) {
    return p.lived_there_while_involved
      ? 'Inactive. Occupancy is history: the record shows the client lived here while the agency was involved.'
      : 'Inactive. Occupancy is history: nothing recorded in the app shows the client lived here while the agency was involved.';
  }
  if (p.client_deceased) return 'The client has died. The property is still held, and the agency still has work on it.';
  if (p.occupied) return 'The client lives here now.';
  return 'The client lives elsewhere and still holds this property.';
}

function Fact({ term, wide, children }: { term: string; wide?: boolean; children: React.ReactNode }) {
  return (
    <div className={wide ? 'sm:col-span-2' : ''}>
      <dt className="text-xs font-bold tracking-[0.1em] text-neutral-500 uppercase">{term}</dt>
      <dd className="whitespace-pre-line text-neutral-900">{children}</dd>
    </div>
  );
}

function when(e: DispositionEvent): string {
  if (e.effective_on) return formatDate(e.effective_on);
  if (e.source === 'Before history') return 'on record before dispositions were dated';
  if (e.source === 'Imported') return "from the agency's Real estate sheet; when it became true is not recorded";
  if (e.source === 'Written directly') return `written directly, ${formatDate(e.created_at.slice(0, 10))}; when it became true is not recorded`;
  return 'date not known';
}

function History({ detail, mayAct }: { detail: PropertyDetail; mayAct: boolean }) {
  const [withdrawing, setWithdrawing] = useState<string | null>(null);
  if (detail.history.length === 0) {
    return <p className="text-sm text-neutral-600">No disposition has been recorded.</p>;
  }
  return (
    <div>
      <h3 className="text-xs font-bold tracking-[0.12em] text-neutral-500 uppercase">Disposition history</h3>
      <ol className="mt-1 divide-y divide-neutral-100">
        {detail.history.map((e) => {
          const withdrawn = e.withdrawn_by_event_id !== null;
          return (
            <li key={e.id} className="py-2">
              <p className={withdrawn ? 'text-neutral-500 line-through' : 'font-medium text-neutral-900'}>
                {e.disposition} <span className="font-normal">· {when(e)}</span>
              </p>
              <p className="text-sm text-neutral-600">
                {e.source === 'Recorded' ? `Recorded by ${e.recorded_by ?? 'someone not on record'}, ${formatDate(e.created_at.slice(0, 10))}` : e.source}
                {e.notes && e.source === 'Recorded' ? ` · ${e.notes}` : ''}
              </p>
              {withdrawn && (
                <p className="text-sm text-neutral-700">
                  Withdrawn{e.withdrawn_at ? ` ${formatDate(e.withdrawn_at.slice(0, 10))}` : ''}: {e.withdrawn_because}
                </p>
              )}
              {withdrawing !== e.id && (
                <RecordEdit may={mayAct && !withdrawn && e.source === 'Recorded'}
                            className="mt-1 mr-3 min-h-[44px] text-sm font-semibold text-neutral-700 underline underline-offset-2"
                            choices={[{ key: 'withdraw', label: 'Withdraw: recorded in error', onPick: () => setWithdrawing(e.id) }]} />
              )}
              {withdrawing === e.id && (
                <WithdrawForm propertyId={detail.property.id} eventId={e.id} onDone={() => setWithdrawing(null)} />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function WithdrawForm({ propertyId, eventId, onDone }: { propertyId: string; eventId: string; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const withdraw = useWithdrawDisposition(propertyId);
  const sending = useRef(false);
  return (
    <form className="mt-2 space-y-2 rounded-lg border border-neutral-300 p-3"
          onSubmit={(ev) => {
            ev.preventDefault();
            if (!reason.trim() || sending.current) return;
            sending.current = true;
            withdraw.mutate({ eventId, reason: reason.trim() }, { onSuccess: onDone, onError: () => { sending.current = false; } });
          }}>
      <p className="text-sm text-neutral-700">
        It stays in the history, marked as withdrawn with your reason, and stops counting. Then record the right one.
      </p>
      <label className={label} htmlFor={`why-${eventId}`}>Why is it being withdrawn?</label>
      <textarea id={`why-${eventId}`} className={`${field} min-h-[72px] py-2`} value={reason} maxLength={4000}
                onChange={(e) => setReason(e.target.value)} />
      {withdraw.error && <Refusal error={withdraw.error} />}
      <div className="flex gap-2">
        <button type="submit" className={primary} disabled={!reason.trim() || withdraw.isPending}>Withdraw it</button>
        <button type="button" className={secondary} onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

/** A dated date, or an explicit "not known": the two are never confused, and neither is guessed. */
function DateOrUnknown({
  id, legend, date, setDate, unknown, setUnknown,
}: {
  id: string; legend: string;
  date: string; setDate: (v: string) => void;
  unknown: boolean; setUnknown: (v: boolean) => void;
}) {
  return (
    <fieldset>
      <legend className={label}>{legend}</legend>
      <input id={id} type="date" className={field} value={date} max={todayLocal()} disabled={unknown}
             onChange={(e) => setDate(e.target.value)} />
      <label className="mt-2 flex min-h-[44px] cursor-pointer items-start gap-3">
        <input type="checkbox" className="mt-1 size-5 shrink-0" checked={unknown}
               onChange={(e) => { setUnknown(e.target.checked); if (e.target.checked) setDate(''); }} />
        <span className="text-neutral-800">The date is not known</span>
      </label>
    </fieldset>
  );
}

function CourtNotice({ p, disposition, needs }: { p: PropertyRecord; disposition: string; needs: string[] }) {
  if (!p.court_auth_required || !needs.includes(disposition)) return null;
  return (
    <div role="note" className="rounded-lg border border-amber-400 bg-amber-50 px-3 py-2 text-amber-950">
      <p className="font-semibold">This needs the court's authority.</p>
      <p className="mt-1 text-sm">
        A sale or surrender of this property is made on a petition to the court. Procura cannot yet record the order
        or check that it exists: make sure the authority is in hand before the property is marked{' '}
        {disposition.toLowerCase()}.
      </p>
    </div>
  );
}

function DispositionForm({ detail, onDone }: { detail: PropertyDetail; onDone: (message: string | null) => void }) {
  const p = detail.property;
  const [disposition, setDisposition] = useState('');
  const [date, setDate] = useState('');
  const [unknown, setUnknown] = useState(false);
  const [notes, setNotes] = useState('');
  const [understood, setUnderstood] = useState(false);
  const record = useRecordDisposition(p.id);
  const sending = useRef(false);
  // Sold and Surrendered end the agency's work: the property goes inactive in
  // the same step, so the form shows what follows before anything is sent.
  const ending = detail.endings.includes(disposition);
  const preview = useInactivePreview(p.id, ending);
  const pv = preview.data?.preview;
  const blocked = ending && pv?.placed_here === true;
  const needsNote = disposition === 'Ended, other';
  const ready = disposition !== '' && (unknown || date !== '') && (!needsNote || notes.trim() !== '')
    && (!ending || (pv !== undefined && !blocked && understood));

  const said = (r: DispositionDone): string => {
    if (r.inactive) {
      const visits = r.site_visits_closed > 0
        ? ` ${r.site_visits_closed} site visit ${r.site_visits_closed === 1 ? 'deadline' : 'deadlines'} closed.`
        : '';
      return `Recorded: ${disposition}. The property is inactive.${visits}${r.past_address_added ? ' A past address was added.' : ''}`;
    }
    return r.disposition === disposition
      ? `Recorded: ${disposition}.`
      : `Recorded in the history. The current disposition is still ${r.disposition ?? 'none'}, because it is dated later.`;
  };

  return (
    <form className={`space-y-3 rounded-lg p-3 ${ending ? 'border-2 border-neutral-900' : 'border border-neutral-300'}`}
          onSubmit={(e) => {
            e.preventDefault();
            if (!ready || sending.current) return;
            sending.current = true;
            record.mutate(
              {
                disposition,
                date_unknown: unknown,
                ...(unknown ? {} : { effective_on: date }),
                ...(notes.trim() ? { notes: notes.trim() } : {}),
                ...(ending ? { confirm_inactive: true } : {}),
              },
              { onSuccess: (r) => onDone(said(r)), onError: () => { sending.current = false; } },
            );
          }}>
      <h3 className="font-semibold text-neutral-900">Record a disposition</h3>
      <p className={hint}>
        It is added to the history; nothing earlier is overwritten. A disposition recorded in error is withdrawn, not
        changed.
      </p>
      <div>
        <label className={label} htmlFor="disp-choice">Disposition</label>
        <select id="disp-choice" className={field} value={disposition}
                onChange={(e) => { setDisposition(e.target.value); setUnderstood(false); }}>
          <option value="">Choose…</option>
          {detail.dispositions.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>
      <CourtNotice p={p} disposition={disposition} needs={detail.needs_court_authority} />

      {ending && !pv && (
        preview.error ? <Refusal error={preview.error} /> : <p className="text-neutral-500">Working out what this would do…</p>
      )}
      {ending && pv && blocked && (
        <div className="space-y-2 rounded-lg border border-neutral-300 p-3">
          <p className="font-semibold text-neutral-900">The client is placed at this property</p>
          <p className="text-neutral-800">
            Record their move first, then mark it {disposition.toLowerCase()}. An inactive property is one the agency has
            let go, and the record must not say the client still lives there.
          </p>
          <Link to={`/clients/${p.client_id}`} state={{ from: 'Real Estate' }} className={`${secondary} inline-flex items-center`}>
            Open the client to record the move
          </Link>
        </div>
      )}
      {ending && pv && !blocked && (
        <div className="space-y-2">
          <p className="text-neutral-800">
            <strong>{disposition} makes the property inactive.</strong> Before you confirm, this is what follows:
          </p>
          <ul className="list-disc space-y-1 pl-5 text-neutral-800">
            <li>
              <strong>It cannot be undone.</strong> The property moves to the record of inactive properties, and its
              dispositions close.
            </li>
            <li>
              {pv.site_visits_open > 0
                ? `This property's initial site visit stops being owed: it closes as not applicable, naming ${disposition.toLowerCase()}.`
                : 'No site visit is open on this property.'}
              {pv.holds_another ? ' Site visits owed on the client\'s other property continue.' : ''}
            </li>
            <li>
              {pv.past_address_exists
                ? 'A past address for it is already on record.'
                : pv.lived_there_while_involved
                  ? 'No past address is added: the record shows the client lived here while the agency was involved.'
                  : 'A past address is added to the client: nothing recorded in the app shows they lived here while the agency was involved.'}
            </li>
          </ul>
        </div>
      )}

      <DateOrUnknown id="disp-date" legend="When did it become true?" date={date} setDate={setDate} unknown={unknown} setUnknown={setUnknown} />
      <div>
        <label className={label} htmlFor="disp-notes">
          Notes <span className="font-normal text-neutral-500">{needsNote ? "— say how it passed out of the agency's hands" : '— optional'}</span>
        </label>
        <textarea id="disp-notes" className={`${field} min-h-[72px] py-2`} value={notes} maxLength={4000}
                  onChange={(e) => setNotes(e.target.value)} />
      </div>
      {ending && pv && !blocked && (
        <label className="flex min-h-[44px] cursor-pointer items-start gap-3">
          <input type="checkbox" className="mt-1 size-5 shrink-0" checked={understood}
                 onChange={(e) => setUnderstood(e.target.checked)} />
          <span className="text-neutral-900">I understand this makes the property inactive and cannot be undone.</span>
        </label>
      )}
      {record.error && <Refusal error={record.error} />}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={!ready || record.isPending}>
          {record.isPending ? 'Recording…' : ending ? `Record ${disposition.toLowerCase()}: make it inactive` : 'Record it'}
        </button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}
